import React from 'react';
import { createStyles } from 'antd-style';

interface TypographyStyle {
  name: string;
  size: string;
  weight: 'Semibold' | 'Medium' | 'Regular';
  lineHeight: string;
  fontSize: number;
  fontWeight: number;
  lineHeightPx: number;
}

const typographyStyles: TypographyStyle[] = [
  // Headings
  {
    name: 'Heading H1',
    size: '40px',
    weight: 'Semibold',
    lineHeight: '48px',
    fontSize: 40,
    fontWeight: 600,
    lineHeightPx: 48,
  },
  {
    name: 'Heading H2',
    size: '36px',
    weight: 'Semibold',
    lineHeight: '44px',
    fontSize: 36,
    fontWeight: 600,
    lineHeightPx: 44,
  },
  {
    name: 'Heading H3',
    size: '32px',
    weight: 'Semibold',
    lineHeight: '40px',
    fontSize: 32,
    fontWeight: 600,
    lineHeightPx: 40,
  },
  {
    name: 'Heading H4',
    size: '28px',
    weight: 'Semibold',
    lineHeight: '36px',
    fontSize: 28,
    fontWeight: 600,
    lineHeightPx: 36,
  },
  {
    name: 'Heading H5',
    size: '24px',
    weight: 'Semibold',
    lineHeight: '32px',
    fontSize: 24,
    fontWeight: 600,
    lineHeightPx: 32,
  },
  {
    name: 'Heading H6',
    size: '20px',
    weight: 'Semibold',
    lineHeight: '24px',
    fontSize: 20,
    fontWeight: 600,
    lineHeightPx: 24,
  },

  // Labels XLarge
  {
    name: 'Label XLarge Semibold',
    size: '18px',
    weight: 'Semibold',
    lineHeight: '28px',
    fontSize: 18,
    fontWeight: 600,
    lineHeightPx: 28,
  },
  {
    name: 'Label XLarge Medium',
    size: '18px',
    weight: 'Medium',
    lineHeight: '28px',
    fontSize: 18,
    fontWeight: 500,
    lineHeightPx: 28,
  },

  // Labels Large
  {
    name: 'Label Large Semibold',
    size: '16px',
    weight: 'Semibold',
    lineHeight: '22px',
    fontSize: 16,
    fontWeight: 600,
    lineHeightPx: 22,
  },
  {
    name: 'Label Large Medium',
    size: '16px',
    weight: 'Medium',
    lineHeight: '22px',
    fontSize: 16,
    fontWeight: 500,
    lineHeightPx: 22,
  },

  // Labels Medium
  {
    name: 'Label Medium Semibold',
    size: '14px',
    weight: 'Semibold',
    lineHeight: '18px',
    fontSize: 14,
    fontWeight: 600,
    lineHeightPx: 18,
  },
  {
    name: 'Label Medium Medium',
    size: '14px',
    weight: 'Medium',
    lineHeight: '18px',
    fontSize: 14,
    fontWeight: 500,
    lineHeightPx: 18,
  },

  // Labels Small
  {
    name: 'Label Small Semibold',
    size: '12px',
    weight: 'Semibold',
    lineHeight: '16px',
    fontSize: 12,
    fontWeight: 600,
    lineHeightPx: 16,
  },
  {
    name: 'Label Small Medium',
    size: '12px',
    weight: 'Medium',
    lineHeight: '16px',
    fontSize: 12,
    fontWeight: 500,
    lineHeightPx: 16,
  },

  // Labels Extra Small
  {
    name: 'Label Extra Small Semibold',
    size: '10px',
    weight: 'Semibold',
    lineHeight: '12px',
    fontSize: 10,
    fontWeight: 600,
    lineHeightPx: 12,
  },
  {
    name: 'Label Extra Small Medium',
    size: '10px',
    weight: 'Medium',
    lineHeight: '12px',
    fontSize: 10,
    fontWeight: 500,
    lineHeightPx: 12,
  },

  // Paragraphs
  {
    name: 'Paragraph XLarge Regular',
    size: '18px',
    weight: 'Regular',
    lineHeight: '22px',
    fontSize: 18,
    fontWeight: 400,
    lineHeightPx: 22,
  },
  {
    name: 'Paragraph Large Regular',
    size: '16px',
    weight: 'Regular',
    lineHeight: '20px',
    fontSize: 16,
    fontWeight: 400,
    lineHeightPx: 20,
  },
  {
    name: 'Paragraph Medium Regular',
    size: '14px',
    weight: 'Regular',
    lineHeight: '18px',
    fontSize: 14,
    fontWeight: 400,
    lineHeightPx: 18,
  },
  {
    name: 'Paragraph Small Regular',
    size: '12px',
    weight: 'Regular',
    lineHeight: '16px',
    fontSize: 12,
    fontWeight: 400,
    lineHeightPx: 16,
  },

  // Overlines
  {
    name: 'OVERLINE MEDIUM',
    size: '14px',
    weight: 'Semibold',
    lineHeight: '18px',
    fontSize: 14,
    fontWeight: 600,
    lineHeightPx: 18,
  },
  {
    name: 'OVERLINE SMALL',
    size: '12px',
    weight: 'Semibold',
    lineHeight: '16px',
    fontSize: 12,
    fontWeight: 600,
    lineHeightPx: 16,
  },
];

const useStyles = createStyles(({ token, css }) => ({
  container: css`
    width: 100%;
  `,
  table: css`
    width: 100%;
    border-collapse: collapse;
  `,
  headerCell: css`
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 400;
    color: ${token.colorTextSecondary};
  `,
  cell: css`
    padding: 20px 16px;
    vertical-align: middle;
  `,
  typeStyleCell: css`
    width: 40%;
  `,
  sizeCell: css`
    width: 15%;
    font-size: 14px;
    color: ${token.colorTextSecondary};
  `,
  weightCell: css`
    width: 20%;
    font-size: 14px;
    color: ${token.colorTextSecondary};
  `,
  lineHeightCell: css`
    width: 15%;
    font-size: 14px;
    color: ${token.colorTextSecondary};
    text-align: right;
    padding-right: 24px;
  `,
}));

const Typography: React.FC = () => {
  const { styles } = useStyles();

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerCell}>Type style</th>
            <th className={styles.headerCell}>Size</th>
            <th className={styles.headerCell}>Weight</th>
            <th
              className={`${styles.headerCell} ${styles.lineHeightCell}`}
              style={{ textAlign: 'right' }}
            >
              Line height
            </th>
          </tr>
        </thead>
        <tbody>
          {typographyStyles.map((style, index) => (
            <tr key={index}>
              <td className={`${styles.cell} ${styles.typeStyleCell}`}>
                <span
                  style={{
                    fontSize: style.fontSize,
                    fontWeight: style.fontWeight,
                    lineHeight: `${style.lineHeightPx}px`,
                    textTransform: style.name.startsWith('OVERLINE') ? 'uppercase' : 'none',
                  }}
                >
                  {style.name}
                </span>
              </td>
              <td className={`${styles.cell} ${styles.sizeCell}`}>{style.size}</td>
              <td className={`${styles.cell} ${styles.weightCell}`}>{style.weight}</td>
              <td className={`${styles.cell} ${styles.lineHeightCell}`}>{style.lineHeight}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Typography;
